'use client'

import React from 'react'
import uniqBy from 'lodash-es/uniqBy'

import { SeparatorSkeleton, useFilter } from '@rewardops/ui'
import { ResponseItem } from '@rewardops/utils'

import { RETAILERS_SORT_OPTIONS } from '~/utils/sort-utils'
import { replacePlaceholders } from '~/utils/translation-utils'
import { useParametersFilter } from '~/components/hooks/filters/use-parameters-filter'
import { useApi } from '~/components/hooks/use-api'
import { PaginationInput } from '~/components/input/pagination-input'
import { GenericSortMenu } from '~/components/maple/filters/generic-sort-menu'
import { RetailerCard } from '~/components/maple/pages/retailers/retailers-card'
import RetailerCardSkeleton from '~/components/maple/pages/retailers/retailers-card-skeleton'
import { SideFilters } from '~/components/maple/side-filters'
import { TopFilters } from '~/components/maple/top-filters'
import { DEFAULT_DESKTOP_PAGE_SIZE } from '~/app/common/constants'
import { useTranslation } from '~/app/i18n/client'
import { RetailersSearchProps } from './retailers-search'

const RetailerItemSkeleton = () =>
  Array.from({ length: DEFAULT_DESKTOP_PAGE_SIZE / 2 }, () => 0).map(
    (_, index) => <RetailerCardSkeleton key={`skeleton-${index + 1}`} />,
  )

const LoadingSkeleton: React.FC = () => {
  return (
    <>
      <SeparatorSkeleton />
      <RetailerItemSkeleton />
    </>
  )
}

const DesktopRetailersSearch = ({
  items,
  sort,
  itemsTotal,
  isItemsLoading: loading,
  earnRatesFilterOptions,
  isFiltersLoading,
  searchQuery,
}: RetailersSearchProps) => {
  const { t } = useTranslation()
  const { tenant } = useApi()

  return (
    <div className="flex">
      {tenant.flags?.earnRateFilterEnabled && (
        <div className="min-w-[16rem] flex-none">
          <SideFilters
            isPontsRangeEnabled={false}
            earnRateOptions={earnRatesFilterOptions}
          />
        </div>
      )}

      <div className="flex-1 px-4 py-4">
        {!loading ? (
          <TopFilters
            headerComponent={
              <div className="flex items-center gap-2">
                <div className="text-3xl font-semibold">
                  {replacePlaceholders(t('search.resultHeader'), {
                    itemsCount: itemsTotal ?? 0,
                    searchQuery,
                  })}
                </div>
              </div>
            }
            sortComponent={
              <GenericSortMenu sortOptions={RETAILERS_SORT_OPTIONS} />
            }
            earnRatesOptions={earnRatesFilterOptions}
          />
        ) : null}

        {loading ? (
          <div className="flex flex-wrap gap-8 py-2">
            <LoadingSkeleton />
          </div>
        ) : itemsTotal !== 0 ? (
          <div className="flex flex-wrap gap-8 py-2">
            {items.map((item: ResponseItem, index: number) => (
              <RetailerCard
                key={`item_${index}_${item?.name}`}
                item={item}
                position={index}
                listName="retailer_search"
              />
            ))}
          </div>
        ) : (
          <div className="px-2 lg:px-0">{t('general.noItems')}</div>
        )}

        {!loading ? (
          <PaginationInput
            className="mt-12"
            totalNumberOfItems={itemsTotal}
            sortOption={sort}
          />
        ) : null}
      </div>
    </div>
  )
}

export default DesktopRetailersSearch
